import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  Clock, MapPin, User, Phone, FileText, Calendar,
  CheckCircle, XCircle, Edit2, Trash2, AlertTriangle,
  Navigation, Users, Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const STATUS_BADGES = {
  scheduled: { variant: "default", label: "Scheduled" },
  confirmed: { variant: "success", label: "Confirmed" },
  in_progress: { variant: "secondary", label: "In Progress" },
  completed: { variant: "outline", label: "Completed" },
  cancelled: { variant: "destructive", label: "Cancelled" },
  missed: { variant: "destructive", label: "Missed" },
  needs_coverage: { variant: "warning", label: "Needs Coverage" },
};

export default function ShiftDetailPanel({ event, onClose, onUpdate }) {
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCallOffDialog, setShowCallOffDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [callOffReason, setCallOffReason] = useState("");

  useEffect(() => {
    loadVisitDetails();
  }, [event]);

  const loadVisitDetails = async () => {
    if (!event?.visit_id) {
      setLoading(false);
      return;
    }
    
    try {
      const { data } = await api.get(`/scheduling/visits/${event.visit_id}`);
      setVisit(data);
    } catch (err) {
      console.error("Failed to load visit:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.put(`/scheduling/visits/${event.visit_id}`, {
        status: "cancelled",
        cancellation_reason: "Cancelled by scheduler",
      });
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Failed to cancel:", err);
      alert("Failed to cancel visit");
    } finally {
      setCancelling(false);
    }
  };

  const handleCallOff = async () => {
    setCancelling(true);
    try {
      await api.post("/scheduling/coverage-event", {
        shift_id: event.visit_id,
        event_type: "call_off",
        reason: callOffReason || "Caregiver called off",
        original_caregiver_id: event.caregiver_id,
      });
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Failed to report call-off:", err);
      alert("Failed to report call-off");
    } finally {
      setCancelling(false);
    }
  };

  const openMaps = (address) => {
    if (address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const data = visit || event;
  const statusInfo = STATUS_BADGES[data.status] || STATUS_BADGES.scheduled;

  return (
    <div className="space-y-6 py-4">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-xl font-bold">{data.client_name || event.title}</h2>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
        </div>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Calendar className="w-4 h-4" />
          <span>{new Date(data.visit_date || event.start?.split("T")[0]).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
        </div>
      </div>

      {/* Time */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="font-semibold">
              {data.start_time} - {data.end_time}
            </div>
            <div className="text-sm text-gray-500">
              {data.duration_hours || event.duration_hours} hours
            </div>
          </div>
        </div>
      </div>

      {/* Caregiver */}
      {data.caregiver_name && (
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="font-medium">{data.caregiver_name}</div>
              <div className="text-sm text-gray-500">Assigned Caregiver</div>
            </div>
          </div>
          <Button variant="outline" size="sm">
            <Phone className="w-4 h-4 mr-1" />
            Call
          </Button>
        </div>
      )}

      {/* Address */}
      {data.client_address && (
        <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
            <MapPin className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="font-medium">Location</div>
            <div className="text-sm text-gray-500">{data.client_address}</div>
            <Button
              variant="link"
              size="sm"
              className="px-0 h-auto text-[#06372B]"
              onClick={() => openMaps(data.client_address)}
            >
              <Navigation className="w-3 h-3 mr-1" />
              Get Directions
            </Button>
          </div>
        </div>
      )}

      {/* Services */}
      {data.services?.length > 0 && (
        <div>
          <h3 className="font-medium mb-2">Services</h3>
          <div className="flex flex-wrap gap-2">
            {data.services.map((service, i) => (
              <Badge key={i} variant="outline">{service}</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {data.status === "scheduled" && (
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <h3 className="font-medium">Actions</h3>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => setShowCallOffDialog(true)}
            >
              <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
              Report Call-Off
            </Button>
            
            <Button
              variant="outline"
              className="justify-start"
            >
              <Users className="w-4 h-4 mr-2 text-blue-500" />
              Reassign
            </Button>
            
            <Button
              variant="outline"
              className="justify-start"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Shift
            </Button>
            
            <Button
              variant="outline"
              className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setShowCancelDialog(true)}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel Shift
            </Button>
          </div>
        </div>
      )}

      {/* Cancel Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this shift?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the visit for {data.client_name} on{" "}
              {new Date(data.visit_date).toLocaleDateString()}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Shift</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelling ? "Cancelling..." : "Cancel Shift"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Call-Off Dialog */}
      <AlertDialog open={showCallOffDialog} onOpenChange={setShowCallOffDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Report Call-Off</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the shift as needing coverage and generate replacement recommendations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Reason</label>
            <textarea
              value={callOffReason}
              onChange={(e) => setCallOffReason(e.target.value)}
              placeholder="Why is the caregiver calling off?"
              className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCallOff}
              disabled={cancelling}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {cancelling ? "Processing..." : "Report Call-Off"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
